import {
  createRouter,
  createWebHistory,
  type NavigationGuard,
  type RouteLocationNormalized,
  type RouteLocationNormalizedGeneric,
} from 'vue-router'
import { isEmpty } from 'lodash'
import { useAppStore } from '@/modules/app/stores/app.store'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useUserStore } from '@/modules/users/stores/user.store'
import { useQueryStore } from '@/modules/services/query/stores/query.store'
import { AuthState } from '@/modules/users/types/user.enums'
import { ROUTE_NAMES } from '@/router/constants/route.constants'

import SearchBar from '@/modules/search/components/SearchBar.vue'

const authCallback = async (to: RouteLocationNormalized) => {
  const code = to.query.code?.toString()
  const state = to.query.state?.toString()

  if (code && state) {
    const authStore = useAuthStore()

    const response = await authStore.exchangeCode(code, state)

    if (response.error) return { name: ROUTE_NAMES.UNAUTHORIZED }
  }

  const queryStore = useQueryStore()
  const gotoRoute = queryStore.getGotoRoute()

  queryStore.removeGotoRoute()

  if (gotoRoute) return gotoRoute

  return { name: ROUTE_NAMES.DASHBOARD }
}

const requireIdentityState = (required: AuthState): NavigationGuard => {
  return async () => {
    const authStore = useAuthStore()

    if (!authStore.identity) await authStore.isIdentityReady()

    if (authStore.identity?.state === required) return true
    else return { name: ROUTE_NAMES.DASHBOARD }
  }
}

const requireIdentity = (): NavigationGuard => {
  return async () => {
    const authStore = useAuthStore()

    if (!authStore.identity) return true
    else return { name: ROUTE_NAMES.EXPIRED }
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: ROUTE_NAMES.DASHBOARD,
      component: () => import('@/modules/dashboard/views/DashboardView.vue'),
      meta: { requiresAuth: true, filterEnabled: true, search: SearchBar },
    },
    {
      path: '/motion-view',
      name: ROUTE_NAMES.MOTION_VIEW,
      component: () => import('@/modules/motion/views/MotionView.vue'),
      meta: { requiresAuth: true, filterEnabled: true, search: SearchBar },
    },
    {
      path: '/user-management',
      name: ROUTE_NAMES.USER_MANAGEMENT,
      component: () => import('@/modules/user-management/views/UserManagementView.vue'),
      meta: { requiresAuth: true, filterEnabled: false },
    },
    {
      path: '/maintenance',
      name: ROUTE_NAMES.MAINTENANCE,
      component: () => import('@/modules/app/views/MaintenanceView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/expired',
      name: ROUTE_NAMES.EXPIRED,
      component: () => import('@/modules/auth/views/ExpiredView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/forbidden',
      name: ROUTE_NAMES.FORBIDDEN,
      component: () => import('@/modules/auth/views/ForbiddenView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/error',
      name: ROUTE_NAMES.ERROR,
      component: () => import('@/modules/auth/views/ErrorView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/access-denied',
      name: ROUTE_NAMES.ACCESS_DENIED,
      component: () => import('@/modules/auth/views/AccessDeniedView.vue'),
      beforeEnter: requireIdentityState(AuthState.AccessDenied),
      meta: { requiresAuth: false },
    },
    {
      path: '/blocked',
      name: ROUTE_NAMES.BLOCKED,
      component: () => import('@/modules/auth/views/BlockedView.vue'),
      beforeEnter: requireIdentityState(AuthState.Blocked),
      meta: { requiresAuth: false },
    },
    {
      path: '/register',
      name: ROUTE_NAMES.REGISTER,
      component: () => import('@/modules/users/views/RegisterView.vue'),
      beforeEnter: requireIdentityState(AuthState.Registration),
      meta: { requiresAuth: false },
    },
    {
      path: '/activate',
      name: ROUTE_NAMES.ACTIVATE,
      component: () => import('@/modules/users/views/ActivateView.vue'),
      beforeEnter: requireIdentityState(AuthState.Activation),
      meta: { requiresAuth: false },
    },
    {
      path: '/callback',
      name: ROUTE_NAMES.CALLBACK,
      component: () => import('@/modules/auth/views/CallbackView.vue'),
      beforeEnter: authCallback,
      meta: { requiresAuth: false },
    },
    {
      path: '/unauthorized',
      name: ROUTE_NAMES.UNAUTHORIZED,
      component: () => import('@/modules/auth/views/UnauthorizedView.vue'),
      beforeEnter: requireIdentity(),
      meta: { requiresAuth: false },
    },
    {
      path: '/service-unavailable',
      name: ROUTE_NAMES.SERVICE_UNAVAILABLE,
      component: () => import('@/modules/auth/views/ServiceUnavailableView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/logout',
      name: ROUTE_NAMES.LOGOUT,
      component: () => import('@/modules/auth/views/LogoutView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/:pathMatch(.*)*',
      name: ROUTE_NAMES.NOT_FOUND,
      component: () => import('@/modules/auth/views/NotFoundView.vue'),
      meta: { requiresAuth: false },
    },
  ],
})

router.beforeEach(async (to: RouteLocationNormalizedGeneric) => {
  if (!to.meta.requiresAuth) return true

  const appStore = useAppStore()
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const queryStore = useQueryStore()

  appStore.isLoading = true

  try {
    queryStore.parseRoute(to.path, to.query)

    if (isEmpty(to.query)) {
      queryStore.removeGotoRoute()
    }

    if (!authStore.identity) {
      await authStore.isIdentityReady()
    }

    const routeLocation = authStore.getRouteLocation(authStore.identity?.state)

    if (!routeLocation) return false

    if (typeof routeLocation === 'object' && 'name' in routeLocation) {
      return routeLocation.name === to.name ? true : routeLocation
    }

    if (!userStore.user) {
      if (await userStore.isCurrentUserReady()) {
        await appStore.isClientReady(to.name)
      }
    }

    return true
  } finally {
    appStore.isLoading = false
  }
})

export default router
